import { useEffect, useRef, useState, type KeyboardEvent, type MouseEvent } from 'react';
import { Link, useParams } from 'react-router-dom';
import { ArrowLeft, FileQuestion, Loader2 } from 'lucide-react';
import { NEWS_FEED_PATH, newsCover } from '../../constants/news.constants';
import { useNewsCtaClick } from '../../hooks/useNewsCtaClick';
import { getHttpStatus, useNewsDetail, useNewsViewerId, useRecordNewsEvents } from '../../hooks/useNewsReader';
import type { NewsPlatform } from '../../types/news.types';
import { getApiErrorMessage } from '../../utils/news.utils';
import { NewsArticle } from '../NewsArticle';
import { NewsLightbox, type LightboxImage } from './NewsLightbox';

/** Marqueur posé sur le corps : les images qu'il contient ouvrent la visionneuse. */
const ZOOMABLE_BODY_CLASS = 'news-body-zoomable';

/**
 * Images du corps qui ouvrent la visionneuse, dans l'ordre du texte. Une image
 * placée dans un lien garde le comportement du lien.
 */
function zoomableImages(root: HTMLElement | null): HTMLImageElement[] {
  if (!root) return [];
  return Array.from(root.querySelectorAll<HTMLImageElement>(`.${ZOOMABLE_BODY_CLASS} img`)).filter(
    (image) => !image.closest('a'),
  );
}

const BACK_LINK =
  'tw-inline-flex tw-items-center tw-gap-1 tw-text-sm tw-font-medium tw-text-affinicar-secondary hover:tw-text-affinicar-primary tw-no-underline';

/**
 * Page d'une nouveauté, à l'URL stable `/nouveautes/<slug>` : contenu complet,
 * bouton d'action, et visionneuse plein écran sur les images du corps.
 *
 * L'ouverture déclare `page_viewed` (c'est ce qui vaut lecture) — l'appel de
 * lecture lui-même n'enregistre rien côté serveur.
 */
export function NewsDetailPage({ platform }: { platform: NewsPlatform }) {
  const { slug } = useParams<{ slug: string }>();
  const viewerId = useNewsViewerId(platform);
  const { data: news, error, isError, refetch } = useNewsDetail(platform, slug);
  const recordEvents = useRecordNewsEvents(platform);
  const handleCtaClick = useNewsCtaClick(platform);

  const articleRef = useRef<HTMLDivElement>(null);
  const [lightbox, setLightbox] = useState<{ images: LightboxImage[]; index: number } | null>(null);

  // Effet pur : la page est affichée → on le déclare, une fois par nouveauté.
  const newsId = news?.id;
  const declaredId = useRef<string | null>(null);
  useEffect(() => {
    if (!newsId || declaredId.current === newsId) return;
    declaredId.current = newsId;
    recordEvents([{ newsId, types: ['page_viewed'] }]);
  }, [newsId, recordEvents]);

  // Effet pur de synchronisation avec le DOM : le corps est du HTML injecté, on
  // ne peut pas poser d'attributs sur ses balises au rendu. On rend ses images
  // atteignables au clavier et annoncées comme des boutons.
  const bodyHtml = news?.bodyHtml;
  useEffect(() => {
    zoomableImages(articleRef.current).forEach((image, position) => {
      const alt = image.getAttribute('alt');
      image.tabIndex = 0;
      image.setAttribute('role', 'button');
      image.setAttribute('aria-label', alt ? `Agrandir l'image : ${alt}` : `Agrandir l'image ${position + 1}`);
    });
  }, [bodyHtml]);

  const openLightboxFrom = (target: EventTarget): boolean => {
    if (!(target instanceof HTMLImageElement)) return false;
    const images = zoomableImages(articleRef.current);
    const index = images.indexOf(target);
    if (index === -1) return false;
    setLightbox({
      images: images.map((image) => ({
        src: image.currentSrc || image.src,
        alt: image.getAttribute('alt') ?? '',
      })),
      index,
    });
    return true;
  };

  const handleArticleClick = (event: MouseEvent<HTMLDivElement>) => {
    openLightboxFrom(event.target);
  };

  const handleArticleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (event.key !== 'Enter' && event.key !== ' ') return;
    if (openLightboxFrom(event.target)) event.preventDefault();
  };

  if (!viewerId) {
    return (
      <div className="tw-max-w-3xl tw-mx-auto tw-py-6">
        <p className="tw-text-sm tw-text-gray-600 tw-mb-0">
          Les nouveautés ne sont pas disponibles pour ce compte.
        </p>
      </div>
    );
  }

  // 404 indifférencié côté serveur : inconnue, dépubliée ou non destinée à ce lecteur.
  if (isError && getHttpStatus(error) === 404) {
    return (
      <div className="tw-max-w-md tw-mx-auto tw-py-16 tw-text-center">
        <div className="tw-inline-flex tw-items-center tw-justify-center tw-w-20 tw-h-20 tw-bg-affinicar-lighter tw-rounded-full tw-mb-4">
          <FileQuestion className="tw-w-10 tw-h-10 tw-text-affinicar-primary" aria-hidden="true" />
        </div>
        <h1 className="tw-text-2xl tw-font-bold tw-text-gray-900 tw-mt-0 tw-mb-2">Nouveauté introuvable</h1>
        <p className="tw-text-sm tw-text-gray-600 tw-mb-6">
          Cette nouveauté n'existe pas, n'est plus publiée ou ne vous est pas destinée.
        </p>
        <Link to={NEWS_FEED_PATH} className={BACK_LINK}>
          <ArrowLeft className="tw-w-4 tw-h-4" aria-hidden="true" />
          Toutes les nouveautés
        </Link>
      </div>
    );
  }

  return (
    <div className="tw-max-w-3xl tw-mx-auto tw-py-6">
      <Link to={NEWS_FEED_PATH} className={`${BACK_LINK} tw-mb-4`}>
        <ArrowLeft className="tw-w-4 tw-h-4" aria-hidden="true" />
        Toutes les nouveautés
      </Link>

      {isError && !news ? (
        <div className="tw-bg-white tw-rounded-lg tw-border tw-border-solid tw-border-red-200 tw-p-6 tw-text-center">
          <p className="tw-text-sm tw-text-red-700 tw-mb-4">
            {getApiErrorMessage(error, 'Impossible de charger cette nouveauté pour le moment.')}
          </p>
          <button
            type="button"
            onClick={() => refetch()}
            className="tw-inline-flex tw-items-center tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-text-gray-700 tw-bg-white tw-border tw-border-solid tw-border-gray-300 tw-rounded-lg hover:tw-bg-gray-50 tw-transition-colors"
          >
            Réessayer
          </button>
        </div>
      ) : !news ? (
        <div className="tw-flex tw-items-center tw-justify-center tw-min-h-[240px]" role="status" aria-label="Chargement de la nouveauté">
          <Loader2 className="tw-w-8 tw-h-8 tw-animate-spin tw-text-affinicar-primary" />
        </div>
      ) : (
        // Délégation : les images du corps viennent de HTML injecté, elles ne
        // peuvent pas porter leurs propres gestionnaires.
        <div
          ref={articleRef}
          onClick={handleArticleClick}
          onKeyDown={handleArticleKeyDown}
          className="tw-bg-white tw-rounded-lg tw-border tw-border-solid tw-border-gray-200 tw-p-5 md:tw-p-8"
        >
          <NewsArticle
            title={news.title}
            shortDescription={news.shortDescription}
            coverImageUrl={newsCover(news).url}
            coverContain={newsCover(news).contain}
            bodyHtml={news.bodyHtml}
            ctaLabel={news.ctaLabel}
            ctaUrl={news.ctaUrl}
            publishedAt={news.publishedAt}
            bodyClassName={`${ZOOMABLE_BODY_CLASS} [&_img]:tw-cursor-zoom-in`}
            onCtaClick={(event) => handleCtaClick(news, event)}
          />
        </div>
      )}

      {lightbox && (
        <NewsLightbox
          images={lightbox.images}
          index={lightbox.index}
          onIndexChange={(index) => setLightbox((current) => (current ? { ...current, index } : current))}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}
